import { ButtonSpeedDial, FloatingLoader } from "@/components";
import useContactPeoples from "@/hooks/useContactPeoples";
import { PeoplePageSearchSchema } from "@/schemas";
import type { ContactMember } from "@/types";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import { createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import ContactCard from "../-components/ContactCard";
import EmptyPeoples from "../-components/EmptyPeoples";
import ListSkeleton from "../-components/ListSkeleton";
import { getContactPeoplesLoader } from "../-functions/loaders/peoples";

export const Route = createFileRoute("/dashboard/contacts/directory/")({
	validateSearch: zodSearchValidator(PeoplePageSearchSchema),
	loader: (ctx) => getContactPeoplesLoader(ctx.context, true),
	pendingComponent: ListSkeleton,
	component: RouteComponent,
	errorComponent: () => {
		const navigate = Route.useNavigate();
		return (
			<div className="container relative h-full py-3 overflow-y-auto">
				<EmptyPeoples
					title="Unlock the Professional Directory"
					message="Upgrade your plan to access verified and pro contacts in the directory. Expand your network today!"
					withButton={true}
					buttonLabel="Upgrade Now"
					buttonProps={{
						startIcon: <ArrowCircleUpIcon />,
						onClick: () =>
							navigate({
								to: "/subscription",
							}),
					}}
				/>
			</div>
		);
	},
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const search = Route.useSearch({
		select: (state: PeoplePageSearchSchema) => state.q,
	});
	const contactPeoples = useContactPeoples({
		isDirectory: true,
		search,
		paddingStart: 16,
		paddingEnd: 229,
	});

	return (
		<>
			<FloatingLoader open={contactPeoples.isLoading} />
			<div
				ref={contactPeoples.scrollRef}
				className="relative h-full py-3 overflow-y-auto"
			>
				{contactPeoples.virtualizer.items.length ? (
					<div
						className="flex flex-col gap-4"
						style={{
							height: contactPeoples.virtualizer.getTotalSize(),
						}}
					>
						{contactPeoples.virtualizer.items.map((memberVirtualizerItem) => {
							const member =
								contactPeoples.peoples[memberVirtualizerItem.index];
							const isMe = member?.id === contactPeoples?.profile?.id;
							return (
								<ContactCard
									key={memberVirtualizerItem.key}
									member={member as ContactMember}
									size={memberVirtualizerItem.size}
									start={memberVirtualizerItem.start}
									isMe={isMe}
								/>
							);
						})}
					</div>
				) : null}
				{contactPeoples.isEmptySearch ? (
					<EmptyPeoples
						title={`No results found for '${search}'`}
						withButton={false}
					/>
				) : null}
				{contactPeoples.isEmpty ? <EmptyPeoples /> : null}
			</div>
			<ButtonSpeedDial
				margin={0}
				bottom={0}
				onClickAdd={() =>
					navigate({
						to: "/dashboard/contacts/no-access",
					})
				}
			/>
		</>
	);
}
